import React, { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { BiChevronDown, BiChevronUp, BiPencil, BiPlus, BiTrash } from "react-icons/bi";
import { LoaderContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import StakehodlerLibraryService from "../../../core/service/StakeholderLibraryService";
import './StakehodlerLibrary.scss';

const StakeholderLibrary = ({ t }) => {

    const { setLoaderDisplay } = useContext(LoaderContext);
    const [parentStakeholders, setParentStakeholders] = useState([]);
    const [parentVisiblity, setParentVisibility] = useState({});
    const [childStakeholdersByParent, setChildStakeholdersByParent] = useState({});
    const [editedStakeholder, setEditedStakeholder] = useState({ id: -1, parentId: -1, action: '' });
    const [stakeHolderDetails, setStakeHolderDetails] = useState({ name: '', type: '' });
    const [validated, setValidated] = useState(false);
    const [deleteParent, setDeleteParent] = useState(false);


    useEffect(() => {
        getParentStakeholders();
    }, []);

    const getParentStakeholders = () => {
        setLoaderDisplay(true);
        StakehodlerLibraryService.getParentStakeholders().then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            res.data.data?.sort((a, b) => a.name.localeCompare(b.name));
            setParentStakeholders(res.data.data);
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const getChildStakeHolders = (id) => {
        setLoaderDisplay(true);
        StakehodlerLibraryService.getChildStakeholders(id).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            res.data.data.children?.sort((a, b) => a.name.localeCompare(b.name));
            setChildStakeholdersByParent(prevState => ({
                ...prevState,
                [id]: res.data.data,
            }));

            setStakeHolderDetails({
                name: res.data.data.name,
                type: res.data.data.type
            });

            setDeleteParent(res.data.data.hasChildren);
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const upsertStakeholder = (e) => {
        e.preventDefault();
        if (!stakeHolderDetails.name || !stakeHolderDetails.type) {
            setValidated(true);
            return;
        }

        const params = {
            id: editedStakeholder.id > 0 ? editedStakeholder.id : null,
            name: stakeHolderDetails.name,
            type: stakeHolderDetails.type,
            parentId: editedStakeholder.parentId > 0 ? editedStakeholder.parentId : null
        }

        setLoaderDisplay(true);
        StakehodlerLibraryService.upsertStakeholder(params).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;

            if (editedStakeholder.action === 'ADD PARENT' || editedStakeholder.action === 'ADD CHILD') {
                toast.success("Stakeholder created successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } else {
                toast.success("Stakeholder updated successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }

            if (editedStakeholder.action === 'ADD PARENT' || editedStakeholder.action === 'EDIT PARENT') {
                getParentStakeholders();
            } else {
                getChildStakeHolders(editedStakeholder.parentId);
            }
            setValidated(false);
            setEditedStakeholder({ id: -1, parentId: -1, action: '' });
            setStakeHolderDetails({ name: '', type: '' });
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const deleteStakeholder = () => {
        setLoaderDisplay(true);
        StakehodlerLibraryService.deleteStakeholder(editedStakeholder.id).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;

            toast.success(res.data.message, {
                position: toast.POSITION.TOP_RIGHT,
            });

            if (editedStakeholder.action === 'DELETE PARENT') {
                getParentStakeholders();
            } else {
                getChildStakeHolders(editedStakeholder.parentId);
            }
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const onDeleteParent = (e, parent) => {
        if (parent.hasChildren || deleteParent) {
            e.preventDefault();
            toast.warning("Delete all the sub categories to continue", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        setEditedStakeholder({ id: parent.id, parentId: -1, action: 'DELETE PARENT' });
    }

    const renderStakeholders = () => {
        return (
            <>
                {
                    parentStakeholders?.map((parent, pIndex) => {
                        return (
                            <>
                                <tr id={`parentCategoryRow-${pIndex}`} key={`parentCategoryRow-${pIndex}`} className="parentCategory-row ">
                                    <td>
                                        <div className="icon_svg cursor" onClick={() => toggleParentVisibility(parent.id)}>
                                            {!parentVisiblity[parent.id] && <BiChevronDown />}
                                            {parentVisiblity[parent.id] === true && <BiChevronUp />}
                                        </div>
                                        <h6>{parent.name}</h6>
                                    </td>
                                    <td>{parent.type === 'INTERNAL' ? 'Internal' : 'External'}</td>
                                    <td className="text-end">
                                        <span className="p-1 cursor"
                                            onClick={() => {
                                                setEditedStakeholder({ id: parent.id, parentId: -1, action: 'EDIT PARENT' });
                                                getChildStakeHolders(parent.id);
                                            }}
                                        >
                                            <BiPencil />
                                        </span>
                                        <span className="p-1 cursor"
                                            onClick={(e) => onDeleteParent(e, parent)}><BiTrash /></span>
                                    </td>
                                </tr>
                                {parentVisiblity[parent.id] === true && renderChildStakeholders(parent, pIndex, childStakeholdersByParent[parent.id])}
                            </>
                        );
                    })
                }
            </>
        );
    };

    const toggleParentVisibility = (parentId) => {
        const p = { ...parentVisiblity };

        if (p[parentId] === true) {
            p[parentId] = false;
        } else {
            p[parentId] = true;
            getChildStakeHolders(parentId);
        }
        setParentVisibility(p);
    };

    const renderChildStakeholders = (parent, pIndex, childStakeholders) => {
        return (
            <>
                {
                    childStakeholders?.children?.map((child, cIndex) => {
                        return (
                            <>
                                <tr id={`childCategoryRow-${pIndex}-${cIndex}`} key={`childCategoryRow-${pIndex}-${cIndex}`}
                                    className="childCategory-row">
                                    <td>
                                        <div className="childCategory_in">
                                            <h6>{child.name}</h6>
                                        </div>
                                    </td>
                                    <td>{child.type === 'INTERNAL' ? 'Internal' : 'External'}</td>
                                    <td className="text-end">
                                        <span className="p-1 cursor"
                                            onClick={() => {
                                                setEditedStakeholder({ id: child.id, parentId: parent.id, action: 'EDIT CHILD' });
                                                getChildStakeHolders(child.id);
                                            }}
                                        >
                                            <BiPencil />
                                        </span>
                                        <span className="p-1 cursor"
                                            onClick={() => setEditedStakeholder({ id: child.id, parentId: parent.id, action: 'DELETE CHILD' })}><BiTrash /></span>
                                    </td>
                                </tr>
                            </>
                        );
                    })
                }
                {
                    <tr className="childCategory-row">
                        <td colSpan={4}>
                            <span className="cursor text-primary fw-600"
                                onClick={() => {
                                    setEditedStakeholder({ id: 0, parentId: parent.id, action: 'ADD CHILD' });
                                    setStakeHolderDetails({ name: '', type: '' });
                                }}
                            >
                                <BiPlus /> {t("SubCategory.label")}</span>
                        </td>
                    </tr>
                }
            </>
        );
    };

    const renderCategoryAndSubCategoryModal = () => {
        if (editedStakeholder.id === -1 || editedStakeholder.action === 'DELETE PARENT' || editedStakeholder.action === 'DELETE CHILD') return <></>;
        return (
            <Modal show={editedStakeholder.id >= 0}
                onHide={() => {
                    setEditedStakeholder({ id: -1, parentId: -1, action: '' });
                    setStakeHolderDetails({ name: '', type: '' });
                    setValidated(false);
                }}
                aria-labelledby="contained-modal-title-vcenter"
                centered>
                <Modal.Header closeButton>
                    < Modal.Title as="h5">
                        {
                            editedStakeholder.action === 'ADD PARENT' ? t("NewCategory.label")
                                : editedStakeholder.action === 'EDIT PARENT' ? t("UpdateCategory.label")
                                    : editedStakeholder.action === 'ADD CHILD' ? t("AddSubCategory.label")
                                        : t("UpdateSubCategory.label")
                        }
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validated}>
                        <div className="row m-0">
                            <div className="col-lg-12 p-2">
                                <label className="mb-1">
                                    {editedStakeholder.action === 'ADD PARENT' || editedStakeholder.action === 'EDIT PARENT'
                                        ? `${t("Category.label")} ${t("Name.label")}`
                                        : `${t("SubCategory.label")} ${t("Name.label")}`} <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={stakeHolderDetails.name}
                                    onChange={(e) => setStakeHolderDetails({ ...stakeHolderDetails, name: e.target.value })}
                                    required
                                    maxLength={250}
                                />
                            </div>
                            <div className="col-lg-12 p-2">
                                <label className="mb-1">{t("Type.label")} <span className="text-danger">*</span></label>
                                <div>
                                    <Form.Check
                                        type="radio"
                                        inline
                                        label={t("Internal.label")}
                                        className="DataTypeCheck"
                                        name="typeck"
                                        id={`pageCheckbox-1`}
                                        checked={stakeHolderDetails.type === 'INTERNAL'}
                                        onChange={() => setStakeHolderDetails({ ...stakeHolderDetails, type: 'INTERNAL' })}
                                        required
                                    />
                                    <Form.Check
                                        type="radio"
                                        inline
                                        label={t("External.label")}
                                        className="DataTypeCheck"
                                        name="typeck"
                                        id={`pageCheckbox-1`}
                                        checked={stakeHolderDetails.type === 'EXTERNAL'}
                                        onChange={() => setStakeHolderDetails({ ...stakeHolderDetails, type: 'EXTERNAL' })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-lg-12 px-2">
                                <div className="col-lg-12 p-2 text-center">
                                    <button
                                        className="btn btn-secondary me-2 px-3"
                                        onClick={() => {
                                            setEditedStakeholder({ id: -1, parentId: -1, action: '' });
                                            setStakeHolderDetails({ name: '', type: '' });
                                            setValidated(false);
                                        }}
                                    >
                                        {" "}
                                        {t("Discard.label")}
                                    </button>
                                    <button
                                        className="btn btn-primary px-4"
                                        onClick={(e) => upsertStakeholder(e)}
                                    >
                                        {" "}
                                        {t("Save.label")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        );
    };

    const renderDeleteCategory = () => {
        if (editedStakeholder.action !== 'DELETE PARENT' && editedStakeholder.action !== 'DELETE CHILD') return <></>;
        return (
            <ConfirmationModal
                modalShow={editedStakeholder.id >= 0 ? true : false}
                messageText={
                    editedStakeholder.action === 'DELETE PARENT' ? t("DeleteCategory.label") : t("DeleteSubCategory.label")
                }
                callbackModal={(val) => {
                    setEditedStakeholder({ id: -1, parentId: -1, action: '' });
                    if (val) {
                        deleteStakeholder();
                    }
                }}
                confirmBtn={t("Confirm.label")}
                CancelBtn={t("Cancel.label")}
            />
        );
    };

    return (
        <div className="StakeholderLibrary">
            <div className="List_header">
                <h5 className="MH">{t("StakeHolderLibrary.label")}</h5>
            </div>
            <div className="row m-0">
                <div className="StakeholderLibrary_card col-lg-12 p-2">
                    <div className="card">
                        <div className="StakeholderLibrary_SetCard">
                            <Table>
                                <tbody>
                                    {renderStakeholders()}
                                </tbody>
                            </Table>
                            <div className="StakeholderLibrary_SetCard_Footer">
                                <div>
                                    <Button variant="secondary"
                                        onClick={() => {
                                            setEditedStakeholder({ id: 0, parentId: 0, action: 'ADD PARENT' });
                                            setStakeHolderDetails({ name: '', type: '' });
                                        }}
                                    >
                                        <BiPlus className="me-1" /> {t("Category.label")}</Button>
                                </div>
                            </div>
                        </div>
                        {renderCategoryAndSubCategoryModal()}
                        {renderDeleteCategory()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(StakeholderLibrary);